import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import LinkButton from 'components/layout/buttons/LinkButton';
import TrashIcon from 'components/layout/icons/TrashIcon';
import translations from 'services/translations/translations';

import 'style/offers/OpportunityLinks.css';

export interface IOfferLink {
  title: string;
  url: string;
}

// An address without a scheme is opened as https, as the server saves it
const toHref = (url: string): string =>
  /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;

// Only web addresses are opened, never e.g. a script
const isWebAddress = (url: string): boolean => {
  if (url.trim() === '') {
    return false;
  }

  try {
    const parsed = new URL(toHref(url));
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (error) {
    return false;
  }
};

/**
 * The web links of a sales opportunity, e.g. the page of the building project or its
 * drawings: a title and the address on a row, opened in a new tab. Saved with the
 * opportunity.
 */
const OpportunityLinks = ({
  links,
  locked,
  setLinks,
}: {
  links: IOfferLink[];
  locked: boolean;
  setLinks: (links: IOfferLink[]) => void;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);
  const tr = (key: string) =>
    t(`${translations.offers}:opportunity.links.${key}`);

  const change = (index: number, changes: Partial<IOfferLink>) =>
    setLinks(
      links.map((link, linkIndex) =>
        linkIndex === index ? { ...link, ...changes } : link
      )
    );

  return (
    <div className="opportunity-links">
      <p className="opportunity-links-title">{tr('Title')}</p>
      {links.map((link, index) => (
        // The links have no ids; they are edited in place
        // eslint-disable-next-line react/no-array-index-key
        <div className="opportunity-link-row" key={index}>
          <Input
            aria-label={tr('Name')}
            className="text-field"
            disabled={locked}
            maxLength={200}
            onChange={(event) => change(index, { title: event.target.value })}
            placeholder={tr('Name')}
            value={link.title}
          />
          <Input
            aria-label={tr('Address')}
            className="text-field"
            disabled={locked}
            maxLength={2000}
            onChange={(event) => change(index, { url: event.target.value })}
            placeholder={tr('Address')}
            value={link.url}
          />
          {isWebAddress(link.url) ? (
            <a
              className="opportunity-link-open"
              href={toHref(link.url)}
              rel="noopener noreferrer"
              target="_blank"
            >
              {tr('Open')}
            </a>
          ) : (
            <span />
          )}
          <div>
            {locked ? null : (
              <TrashIcon
                style={{ marginTop: '0.5rem' }}
                onClick={() =>
                  setLinks(links.filter((_, linkIndex) => linkIndex !== index))
                }
              />
            )}
          </div>
        </div>
      ))}
      {locked ? null : (
        <LinkButton
          onClick={() => setLinks([...links, { title: '', url: '' }])}
        >
          {`+ ${tr('Add')}`}
        </LinkButton>
      )}
    </div>
  );
};

export default OpportunityLinks;
